import { useState, useRef, useEffect } from 'react'
import cutileiApi from 'services/cutileiApi'
import BankingService from 'services/Banking'
import MoneyInput from 'components/Inputs/MoneyInput'
import TextInput from 'components/Inputs/TextInput'
import DateInput from 'components/Inputs/DateInput'
import Select from 'components/Inputs/Select'
import AlertDialog from 'components/AlertDialog'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import bankingPixTransferValidator from 'validators/bankingPixTransferValidator'
import { Formik } from 'formik'
import { DateTime } from 'luxon'
import { MdPix } from 'react-icons/md'

import {
  Form,
  Row,
  FormField,
  FieldLabel,
  FormActions,
  HighlightRow,
  CardHeader,
  CardTitle,
  Caption,
  DataList,
  DataRow,
  DataLabel,
  DataValue,
  MoneyTextMask,
  StatusBadge,
  Divider,
  WarningText,
  MaskedInput,
  InfoText,
  PlainMoneyText
} from '../../styles'

const formatDate = date => DateTime.fromISO (date).toFormat ('dd/MM/yyyy')

const formatMoney = value => Number (value).toLocaleString ('pt-BR', {
  style: 'currency',
  currency: 'BRL'
})

// Agendamento só faz sentido para hoje em diante.
const isPastDate = ({ date }) => (
  DateTime.fromJSDate (date).startOf ('day') < DateTime.now ().startOf ('day')
)

/*
  O formulário tem duas etapas: preencher e conferir.

  A Asaas não expõe consulta de chave Pix de terceiro (DICT) — `/pix/addressKeys` lê apenas
  as chaves da própria conta —, então não há como mostrar o nome de quem vai receber. A
  conferência que resta é a da chave digitada, e é justamente por ela ser a única que existe
  que vale uma etapa inteira: a segunda tela mostra chave, tipo, tarifa, total e o saldo que
  sobra, antes do diálogo de confirmação. Um Pix enviado para a chave errada não volta.

  A etapa não é uma tela à parte, como `PixReview` e `BillReview` nos apps. Lá a leitura vem
  de fora — câmera, copia e cola —, e há várias portas de entrada; aqui há uma só, e passar
  os valores adiante seria pior do que mantê-los onde já estão.
*/
/*
  Tradução do nome semântico de `BankingService.PIX_KEY_INPUTS` para os blocos do Cleave.
  O nome mora no serviço, compartilhado com os apps; a forma de aplicá-lo é de cada front.
*/
/*
  Quanto o campo espera antes de adivinhar o tipo da chave. 300 ms é o valor da casa — o
  mesmo de `SearchInput`, `PixCodeForm` e `PixQrForm` —, e não há por que divergir aqui.
*/
const GUESS_DELAY = 300

const CLEAVE_OPTIONS = {
  cpf: { blocks: [3, 3, 3, 2], delimiters: ['.', '.', '-'], numericOnly: true },
  cnpj: { blocks: [2, 3, 3, 4, 2], delimiters: ['.', '.', '/', '-'], numericOnly: true },
  cellphone: { blocks: [0, 2, 5, 4], delimiters: ['(', ') ', '-'], numericOnly: true }
}

export default function PixForm ({
  availableBalance,
  pixFee = 0,
  requestConfig,
  onCompleted = () => {}
}) {
  const [sending, setSending] = useState (false)
  const [receipt, setReceipt] = useState (null)
  const [showConfirmDialog, setShowConfirmDialog] = useState (false)
  const [showOkDialog, setShowOkDialog] = useState (false)
  const [requestError, setRequestError] = useState (null)

  /* Em qual das duas etapas o formulário está: preencher ou conferir. */
  const [reviewing, setReviewing] = useState (false)

  /*
    Enquanto o titular não escolhe o tipo, ele é adivinhado a partir do que está sendo
    digitado. No primeiro toque no seletor a detecção se cala neste formulário: escolha vale
    mais que palpite, e um seletor que se mexe sozinho depois de escolhido é pior do que um
    que não ajuda.
  */
  const [keyTypeChosen, setKeyTypeChosen] = useState (false)
  const guessTimer = useRef (null)

  useEffect (() => () => clearTimeout (guessTimer.current), [])

  /*
    Adivinhar **não apaga a chave** — é a diferença em relação à troca manual, logo abaixo,
    que apaga. Ali o titular disse que o que estava no campo é de outro tipo; aqui ele acabou
    de digitar aquilo, e o que muda é só o tipo, com a máscara que vem junto.
  */
  const guessKeyType = (text, setFieldValue) => {
    if (keyTypeChosen) return

    clearTimeout (guessTimer.current)

    guessTimer.current = setTimeout (() => {
      const guess = BankingService.guessPixKeyType (text)

      if (!guess) return

      setFieldValue ('keyType', BankingService.getPixKeyType (guess))

      /*
        A máscara do campo é da tela; quem vai para o diálogo é o valor guardado aqui. Sem
        esta linha o palpite trocava a máscara e deixava `29537995593` na conferência.
      */
      setFieldValue ('key', BankingService.formatPixKey (guess, text))
    }, GUESS_DELAY)
  }

  const toggleConfirmDialog = () => setShowConfirmDialog (prev => !prev)
  const toggleOkDialog = () => setShowOkDialog (prev => !prev)

  const sendPix = async (values, resetForm) => {
    try {
      setSending (true)
      setRequestError (null)

      const key = BankingService.normalizePixKey (values.keyType.value, values.key)

      const { data: transaction } = await cutileiApi.post ('/banking_account/pix_transfers', {
        pixAddressKey: key,
        pixAddressKeyType: values.keyType.value,
        value: values.value,
        ...values.scheduleDate && {scheduleDate: values.scheduleDate},
        ...values.description && {description: values.description}
      }, requestConfig)

      setReceipt ({...transaction, recipientKey: values.key})
      // Limpa a chave junto: um Pix enviado não pode ser repetido por engano.
      resetForm ()
      // Formulário novo, palpite novo: o próximo Pix pode ser para outro tipo de chave.
      setKeyTypeChosen (false)
      setReviewing (false)
      toggleOkDialog ()
      onCompleted ()
    } catch (error) {
      console.log (error.response ?? error)
      setRequestError (
        error.response?.data?.message ?? 'Não foi possível enviar o Pix'
      )
    } finally {
      setSending (false)
    }
  }

  return (
    <Formik
      validationSchema={bankingPixTransferValidator}
      /*
        O saldo é recarregado após cada Pix: sem reinicializar, o teto da validação
        continuaria sendo o saldo de antes da operação.
      */
      enableReinitialize
      initialValues={{
        availableBalance,
        keyType: null,
        key: '',
        value: 0,
        description: '',
        scheduleDate: null
      }}
      /*
        O `onSubmit` avança para a conferência, e não envia: é a validação do Formik que
        decide se o formulário está bom o bastante para ser conferido. O envio acontece dois
        gestos depois — botão da conferência, e depois o diálogo.

        O `setSubmitting (false)` é obrigatório porque este handler é síncrono: o Formik só
        desliga `isSubmitting` sozinho quando o handler devolve promessa. Sem ele o botão da
        primeira etapa fica girando e desabilitado ao voltar da conferência.
      */
      onSubmit={(_, { setSubmitting }) => {
        setReviewing (true)
        setSubmitting (false)
      }}
    >
      {({
        values,
        errors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldTouched,
        handleChange,
        handleBlur,
        handleSubmit,
        resetForm
      }) => {
        /*
          A tarifa sai da conta junto com o valor, e é cotada antes — `pixFee` chega com o
          saldo. Sem somá-la aqui, a conferência repetiria o que o titular digitou e a
          transferência poderia ser recusada por saldo depois de ele ter confirmado.
        */
        const total = Number (values.value) + Number (pixFee)
        const canTransfer = total <= availableBalance

        /* Usado na conferência e no diálogo; calculado uma vez para caber nos dois. */
        const keyTypeLabel = BankingService.getPixKeyTypeLabel (values.keyType?.value)

        return (
          <Form onSubmit={handleSubmit}>
            <CardHeader>
              <CardTitle>Transferir por Pix</CardTitle>
              <MdPix size={18} color='#FF9800'/>
            </CardHeader>
            <Caption>
              {reviewing
                ? 'Confira os dados abaixo antes de enviar'
                : 'Preencha os dados do Pix — você confere tudo na etapa seguinte'}
            </Caption>
            <Divider/>
            {!reviewing ? (
              <>
                <Row>
                  <FormField>
                    <FieldLabel>Tipo de chave</FieldLabel>
                    <Select
                      name='keyType'
                      placeholder='Selecione'
                      options={BankingService.PIX_KEY_TYPES}
                      value={values.keyType}
                      error={errors.keyType}
                      touched={touched.keyType}
                      /*
                        Trocar o tipo apaga a chave. Um CPF que sobra no campo depois de escolher
                        "E-mail" só pode dar em erro de validação ou, pior, num Pix para a chave
                        errada — que não volta.
                      */
                      onChange={(name, option) => {
                        setKeyTypeChosen (true)
                        clearTimeout (guessTimer.current)
                        setFieldValue (name, option)
                        setFieldValue ('key', '')
                        setFieldTouched ('key', false)
                      }}
                      onBlur={setFieldTouched}
                    />
                    {touched.keyType && errors.keyType && (
                      <WarningText>{errors.keyType}</WarningText>
                    )}
                  </FormField>
                  <FormField stretch={2}>
                    <FieldLabel>Chave Pix do destinatário</FieldLabel>
                    {(() => {
                      const input = BankingService.getPixKeyInput (values.keyType?.value)
                      const options = CLEAVE_OPTIONS[input.mask]

                      /*
                        E-mail e chave aleatória seguem em campo livre: o primeiro não tem forma
                        fixa e a segunda é hexadecimal, e o Cleave numérico comeria as letras dela.
                      */
                      if (!options) return (
                        <TextInput
                          name='key'
                          placeholder={input.placeholder}
                          value={values.key}
                          onChange={event => {
                            handleChange ('key') (event)
                            guessKeyType (event.target.value, setFieldValue)
                          }}
                          onBlur={handleBlur ('key')}
                        />
                      )

                      return (
                        <MaskedInput
                          /*
                            Remontado a cada troca de tipo, e é obrigatório: o Cleave lê `options`
                            uma vez, na construção, e ignora mudanças depois. Sem esta chave o
                            exemplo trocava e a máscara não — quem escolhia CNPJ depois de CPF
                            digitava catorze dígitos vendo um CPF se formar.
                          */
                          key={input.mask}
                          name='key'
                          placeholder={input.placeholder}
                          options={options}
                          value={values.key}
                          /*
                            Guarda o valor **pontuado**, e não o `rawValue` que o resto do site
                            usa: é esta string que vai para o diálogo de confirmação, a última
                            defesa contra um Pix para a chave errada. "295.379.955-93" se confere
                            de relance; "29537995593", não.

                            O envio não muda: `BankingService.normalizePixKey` tira a máscara antes
                            de a chave sair, e os testes de formato do validador já toleram
                            pontuação.
                          */
                          onChange={event => {
                            setFieldValue ('key', event.target.value)
                            guessKeyType (event.target.value, setFieldValue)
                          }}
                          onBlur={handleBlur ('key')}
                        />
                      )
                    }) ()}
                    {touched.key && errors.key && (
                      <WarningText>{errors.key}</WarningText>
                    )}
                  </FormField>
                </Row>
                <Row>
                  <FormField>
                    <FieldLabel>Valor</FieldLabel>
                    <MoneyInput
                      name='value'
                      placeholder='R$ 0,00'
                      value={values.value}
                      maxValue={availableBalance}
                      onValueChange={setFieldValue}
                      onBlur={handleBlur ('value')}
                    />
                    {touched.value && errors.value && (
                      <WarningText>{errors.value}</WarningText>
                    )}
                  </FormField>
                  <FormField>
                    <FieldLabel>Agendar para (opcional)</FieldLabel>
                    <DateInput
                      placeholder='Enviar agora'
                      value={values.scheduleDate}
                      clearable={true}
                      disabledDates={isPastDate}
                      onChange={date => setFieldValue ('scheduleDate', date)}
                      style={{width: '100%'}}
                    />
                  </FormField>
                </Row>
                <Row>
                  <FormField>
                    <FieldLabel>Descrição (opcional)</FieldLabel>
                    <TextInput
                      name='description'
                      placeholder='Aparece no seu extrato'
                      value={values.description}
                      onChange={handleChange ('description')}
                      onBlur={handleBlur ('description')}
                    />
                    {touched.description && errors.description && (
                      <WarningText>{errors.description}</WarningText>
                    )}
                  </FormField>
                </Row>
                {requestError && <WarningText>{requestError}</WarningText>}
                <HighlightRow>
                  <DataLabel>Você vai transferir</DataLabel>
                  <PlainMoneyText value={Number (values.value)}/>
                </HighlightRow>
                <FormActions>
                  <MainButton
                    label='Continuar'
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    width={170}
                  />
                </FormActions>
              </>
            ) : (
              <>
                <DataList>
                  <DataRow>
                    <DataLabel>Tipo de chave</DataLabel>
                    <DataValue>{keyTypeLabel}</DataValue>
                  </DataRow>
                  {/*
                    A chave aparece pontuada e inteira: sem favorecido para conferir, ela é o
                    único dado desta tela que diz para quem o dinheiro vai.
                  */}
                  <DataRow>
                    <DataLabel>Chave</DataLabel>
                    <DataValue>{values.key}</DataValue>
                  </DataRow>
                  <DataRow>
                    <DataLabel>Valor</DataLabel>
                    <MoneyTextMask value={Number (values.value)}/>
                  </DataRow>
                  <DataRow>
                    <DataLabel>Tarifa do Pix</DataLabel>
                    <MoneyTextMask value={pixFee}/>
                  </DataRow>
                  {values.scheduleDate && (
                    <DataRow>
                      <DataLabel>Agendado para</DataLabel>
                      <DataValue>{formatDate (values.scheduleDate)}</DataValue>
                    </DataRow>
                  )}
                  {values.description && (
                    <DataRow>
                      <DataLabel>Descrição</DataLabel>
                      <DataValue>{values.description}</DataValue>
                    </DataRow>
                  )}
                </DataList>
                <HighlightRow>
                  <DataLabel><b>Total a debitar</b></DataLabel>
                  <PlainMoneyText value={total}/>
                </HighlightRow>
                <DataList>
                  <DataRow>
                    <DataLabel>Saldo depois desta transferência</DataLabel>
                    <MoneyTextMask value={availableBalance - total}/>
                  </DataRow>
                </DataList>
                {/*
                  O aviso vive aqui, e não na primeira etapa: é diante dos números fechados que
                  ele pesa, e não enquanto ainda se digita.
                */}
                <WarningText>
                  {'Um Pix enviado para a chave errada não pode ser desfeito'}
                </WarningText>
                {!canTransfer && (
                  <WarningText>
                    {'O valor com a tarifa é maior que o seu saldo disponível'}
                  </WarningText>
                )}
                {requestError && <WarningText>{requestError}</WarningText>}
                <FormActions>
                  <SecondaryButton
                    label='Voltar'
                    onClick={() => setReviewing (false)}
                    disabled={sending}
                    color='#606060'
                    width={110}
                  />
                  <MainButton
                    label={values.scheduleDate ? 'Agendar Pix' : 'Transferir'}
                    onClick={toggleConfirmDialog}
                    disabled={!canTransfer}
                    loading={sending}
                    width={170}
                  />
                </FormActions>
              </>
            )}
            {receipt && (
              <>
                <Divider/>
                <Row style={{justifyContent: 'space-between', alignItems: 'center'}}>
                  <InfoText>{`Último Pix para ${receipt.recipientKey}`}</InfoText>
                  <StatusBadge color={BankingService.getTransactionStatusColor (receipt.status)}>
                    {BankingService.getTransactionStatusLabel (receipt.status)}
                  </StatusBadge>
                </Row>
              </>
            )}
            <AlertDialog
              visible={showConfirmDialog}
              title={values.scheduleDate ? 'Confirmar agendamento' : 'Confirmar Pix'}
              /*
                A chave aparece por extenso: sem o nome do favorecido, ela é a única coisa
                que o titular tem para conferir antes de o dinheiro sair.
              */
              message={
                `${values.scheduleDate ? 'Agendar' : 'Transferir'} `
                + `${formatMoney (values.value)} para a chave `
                + `${values.keyType ? keyTypeLabel : ''} `
                + `${values.key}`
                + `${values.scheduleDate ? ` em ${formatDate (values.scheduleDate)}` : ''}?`
              }
              confirmText={values.scheduleDate ? 'Agendar' : 'Transferir'}
              cancelText='Cancelar'
              /*
                Sem fechar o diálogo aqui: o `AlertDialog` já chama `onClose` depois do
                `onConfirm`. Alternar de novo fazia os dois updaters rodarem em sequência —
                `true`, `false`, `true` — e a confirmação ficava aberta atrás do aviso de Pix
                enviado, como se nada tivesse acontecido.
              */
              onConfirm={() => sendPix (values, resetForm)}
              onClose={toggleConfirmDialog}
            />
            <AlertDialog
              visible={showOkDialog}
              title={receipt?.scheduledAt ? 'Pix agendado' : 'Pix enviado'}
              message={
                receipt?.scheduledAt
                  ? 'O Pix será enviado na data agendada e o saldo debitado no dia'
                  : 'O Pix foi enviado e o saldo já foi debitado'
              }
              confirmText='Ok'
              onClose={toggleOkDialog}
            />
            </Form>
        )
      }}
    </Formik>
  )
}
